import { GetStaticPropsContext } from 'next';
import { NextRouter } from 'next/router';

import { awaitBuilderData } from '@/builder';
import { setCookie } from '@/helpers/cookieUtils';
import { BusinessUrlPrefix, getLocaleFromRoute, Locale, LOCALE_LINKS, localifyLink, stripLocaleFromURL } from '@/helpers/LangHelper';
import ArticleModel from '@/models/ArticleModel';
import BusinessHubModel from '@/models/BusinessHub';
import PageModel from '@/models/Page';
import { Category } from '@/types/Category';

export enum BuilderModel {
    PAGE = 'page',
    BUSINESS_HUB = 'business-hub',
    ARTICLE = 'article',
}

const FETCH_ALL_LIMIT = 100;

const getPages = async (includeUnpublished?: boolean, offset = 0, locale?: Locale) => {
    let currentPages = await PageModel.getAll({
        includeUnpublished: includeUnpublished ?? false,
        fields: 'query,data.url',
        limit: FETCH_ALL_LIMIT,
        locale,
    });

    if (currentPages.length == FETCH_ALL_LIMIT) {
        currentPages = [...currentPages, ...(await getPages(includeUnpublished, offset + FETCH_ALL_LIMIT, locale))];
    }

    return currentPages;
};

const getBusinessHubPages = async (includeUnpublished?: boolean, offset = 0, locale?: Locale) => {
    let currentPages = await BusinessHubModel.getAll({
        includeUnpublished: includeUnpublished ?? false,
        fields: 'query,data.url',
        limit: FETCH_ALL_LIMIT,
        locale,
    });

    if (currentPages.length == FETCH_ALL_LIMIT) {
        currentPages = [...currentPages, ...(await getBusinessHubPages(includeUnpublished, offset + FETCH_ALL_LIMIT, locale))];
    }

    return currentPages;
};

const getArticles = async (includeUnpublished?: boolean, offset = 0, locale?: Locale) => {
    let currentPages = await ArticleModel.findAll({
        includeUnpublished: includeUnpublished ?? false,
        fields: 'query,data.url,data.categories',
        filters: {
            limit: FETCH_ALL_LIMIT,
            offset,
            locale,
        },
    });

    if (currentPages.length == FETCH_ALL_LIMIT) {
        currentPages = [...currentPages, ...(await getArticles(includeUnpublished, offset + FETCH_ALL_LIMIT, locale))];
    }

    return currentPages;
};

async function getPageStaticPaths(includeUnpublished?: boolean, locale?: Locale) {
    const pages = await getPages(includeUnpublished, 0, locale);
    const businessHubPages = await getBusinessHubPages(includeUnpublished, 0, locale);

    const allPaths: string[] = [];

    [...pages, ...businessHubPages].forEach((page) => {
        // if (!EXCLUDE_PAGES.includes(page.data?.url)) {
        for (const queryParam of (page as any).query) {
            // Query is definitely a parameter. Seems like the types aren't updated.
            if (queryParam.property === 'locale') {
                for (const pageLocale of queryParam.value) {
                    allPaths.push(localifyLink(`/${pageLocale}${page?.data?.url}`, pageLocale));
                }
            }
        }
        // }
    });

    return {
        paths: allPaths,
        fallback: true,
    };
}

async function getArticleStaticPaths(includeUnpublished?: boolean, locale?: Locale) {
    const articles = await getArticles(includeUnpublished, 0, locale);

    const pathArray: {
        lang: Locale;
        category: string;
        article: string;
        business: string;
    }[] = [];

    articles.map((article) => {
        const page = ArticleModel.fromBuilderContent(article);
        const queryFilters = (article as any).query;

        // Check if page has lang
        if (queryFilters.length > 0 && queryFilters[1].property == 'locale') {
            queryFilters[1].value.forEach((lang: Locale) => {
                page.categories.forEach((category: Category) => {
                    if (category.categoryUrl) {
                        const businessPath = LOCALE_LINKS[lang]?.businessRoot;

                        if (!locale || lang === locale) {
                            pathArray.push({
                                lang,
                                category: category.categorySlug ?? '',
                                article: page.articleSlug,
                                business: businessPath ?? BusinessUrlPrefix.Business,
                            });
                        }
                    }
                });
            });
        }
    });

    return pathArray;
}

export async function createArticleStaticPaths(includeUnpublished?: boolean, locale?: Locale) {
    const paths = await getArticleStaticPaths(includeUnpublished, locale);

    const allPaths = paths.map((link) => {
        return { params: link };
    });

    return {
        paths: allPaths,
        fallback: true,
    };
}

export async function createArticleStaticProps({
    params,
    includeUnpublished,
}: GetStaticPropsContext<any> & {
    includeUnpublished?: boolean;
}) {
    const lang = Locale.USA;
    const page = await ArticleModel.getArticle(params['article'], lang, includeUnpublished);
    const article = ArticleModel.fromBuilderContent(page);
    const relatedArticles = await ArticleModel.getRelatedArticles(article, lang);

    await awaitBuilderData(page, lang);

    const { business, category, article: articleSlug } = params;
    let pageSlug = stripLocaleFromURL([lang, business, 'access', category, articleSlug].join('/') ?? '');

    let canonicalUrl = null;
    const host = LOCALE_LINKS[lang as Locale]?.host;
    if (host) {
        canonicalUrl = `${host}/${lang}${pageSlug}`;
    }

    return {
        props: { article: article || null, relatedArticles, canonicalUrl },
        revalidate: 1800, // Half an hour
    };
}

export async function createStaticPaths(includeUnpublished?: boolean, locale?: Locale) {
    const paths = (await getPageStaticPaths(includeUnpublished, locale)).paths;
    return {
        paths,
        fallback: true,
    };
}

export async function createStaticProps({
    params,
    includeUnpublished,
}: GetStaticPropsContext<any> & {
    includeUnpublished?: boolean;
}) {
    const slug = params?.slug;
    const pageSlug = Array.isArray(slug) ? slug.join('/') : slug;
    const lang: Locale = params.slug[0];
    const localifySlug = localifyLink(stripLocaleFromURL(pageSlug), Locale.Australia);
    const strippedLocaleURL = stripLocaleFromURL(slug.join('/') ?? '');

    let model = BuilderModel.PAGE;
    let page = await PageModel.getPage(localifySlug, lang);

    if (!page) {
        page = await BusinessHubModel.getPage(localifySlug, lang);
        if (page) model = BuilderModel.BUSINESS_HUB;
    }

    if (page) await awaitBuilderData(page, lang);

    let canonicalUrl = null;
    const host = LOCALE_LINKS[lang]?.host;
    if (host) {
        canonicalUrl = `${host}/${lang}${strippedLocaleURL}`;
    }

    return {
        props: { page: page ?? null, model, canonicalUrl },
        revalidate: 3600,
    };
}

export async function getBuilderPreviewPage(router: NextRouter) {
    const locale: Locale = getLocaleFromRoute(router.asPath);
    const pageId = router.query[`builder.overrides.${BuilderModel.PAGE}`] as string;
    if (pageId) {
        const page = await PageModel.findById(pageId as string, true);
        if (page) {
            await awaitBuilderData(page, locale);

            return {
                page: page,
                model: BuilderModel.PAGE,
            };
        }
    }

    const businessHubId = router.query[`builder.overrides.${BuilderModel.BUSINESS_HUB}`] as string;
    if (businessHubId) {
        const testVariant = router.query[`builder.tests.${businessHubId}`];
        if (testVariant) {
            setCookie(`builder.tests.${businessHubId}`, `${testVariant}`);
        }

        const businessHubPage = await BusinessHubModel.findById(businessHubId);
        if (businessHubPage) {
            await awaitBuilderData(businessHubPage, locale);

            return {
                page: businessHubPage,
                model: BuilderModel.BUSINESS_HUB,
            };
        }
    }
}
